import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import GridContainer from '../../../components/Grid/GridContainer';
import GridItem from '../../../components/Grid/GridItem';
import Typography from '../../../components/Typography/CustomTypography';
import styles from '../../../assets/jss/pages/adminSettingCustomColorStyle';
import WrapperContainer from '../../../components/Container/WrapperContainer';
import Validation from '../../../common/Validation';
import Button from '../../../components/Button/CustomButton';
import ListItem from './ItemSettingColor';
import CleanableInput from '../../../components/Input/CleanableInput';
import Snackbar from '../../../components/Snackbar/Snackbar';
import AlertDialog from '../../../components/Dialog/AlertDialog';

const useStyles = makeStyles(styles);

const SettingCustomColor = ({
    getSettingCustomColor,
    updateSettingCustomColor,
    clearGetSettingColor,
    clearUpdateSettingColor,
    customColor,
    updateCustomColor,
}) => {
    const classes = useStyles();
    const { shortName } = useParams();
    const intl = useIntl();
    const [params, setParams] = useState({});
    const [validateResults, setValidateResults] = useState({});
    const [listCustomColor, setListCustomColor] = useState(ListItem);
    const [isOpenSnackbar, setIsOpenSnackbar] = useState({ isOpen: false, message: '' });
    const [isOpenConfirmSuccess, setIsOpenConfirmSuccess] = useState({ open: false, content: '', type: '' });

    useEffect(() => {
        getSettingCustomColor({
            site: 0,
            eventShortName: shortName,
        });
        return () => {
            clearGetSettingColor();
            clearUpdateSettingColor();
        };
    }, []);

    useEffect(() => {
        if (!_.isEmpty(customColor)) {
            setParams(customColor.content);
            setListCustomColor(ListItem(customColor.content));
        }
    }, [customColor]);

    useEffect(() => {
        if (!_.isEmpty(updateCustomColor)) {
            setIsOpenSnackbar({ isOpen: true, message: '更新しました。' });
            getSettingCustomColor({
                site: 0,
                eventShortName: shortName,
            });
            clearUpdateSettingColor();
        }
    }, [getSettingCustomColor, shortName, updateCustomColor, clearUpdateSettingColor]);

    useEffect(() => {
        setListCustomColor(ListItem(params));
    }, [params]);

    const handleSetParams = (field, fieldSub, value) => {
        if (_.isEmpty(fieldSub)) {
            setParams((prevParams) => ({ ...prevParams, [field]: value }));
        } else setParams((prevParams) => ({ ...prevParams, [field]: { ...prevParams[field], [fieldSub]: value } }));
    };

    const validate = () => {
        const validateRules = {};
        // eslint-disable-next-line array-callback-return
        listCustomColor.map((item, index) => {
            validateRules[`${item.key}_${index}`] = [() => item.value && Validation.validateHexColor(item.value)];
        });
        const validateResult = Validation.validateAllRules(validateRules);

        if (Object.keys(validateResult).length > 0) {
            setValidateResults({ ...validateResult });
            window.scrollTo(0, 0);
            return false;
        }
        setValidateResults({});
        return true;
    };

    const handleSubmit = () => {
        if (validate()) {
            setIsOpenConfirmSuccess({ open: true, content: '適用して宜しいですか。', type: 'submit' });
        }
    };

    const handleChange = (value, item) => {
        handleSetParams(item.key, item.keySub, value.trim());
    };

    const handleReset = () => {
        setValidateResults({});
        updateSettingCustomColor({
            eventShortName: shortName,
            data: {
                site: 0,
                content: {},
            },
        });
    };

    const handleCancel = () => {
        setParams(customColor.content);
        setValidateResults({});
    };

    const handleSuccessDialog = (type) => {
        setIsOpenConfirmSuccess({ open: false, content: '', type: '' });
        if (type === 'submit') {
            updateSettingCustomColor({
                eventShortName: shortName,
                data: {
                    site: 0,
                    content: params,
                },
            });
        } else if (type === 'reset') {
            handleReset();
        } else if (type === 'cancel') {
            handleCancel();
        }
    };

    return (
        <>
            <GridContainer className={classes.custom}>
                <WrapperContainer>
                    <GridContainer className={classes.customSite}>
                        <GridItem md={3} xs={3}>
                            <Typography bold>反映するサイト</Typography>
                        </GridItem>
                        <GridItem md={4} xs={4}>
                            <Typography bold>色を変更する部分</Typography>
                        </GridItem>
                        <GridItem md={5} xs={5}>
                            <Typography bold>色を選択</Typography>
                        </GridItem>
                    </GridContainer>
                    {listCustomColor.map((item, index) => (
                        <GridContainer className={classes.customSite}>
                            {!index ? (
                                <GridItem md={3} xs={3}>
                                    <Typography bold>入場者サイト</Typography>
                                </GridItem>
                            ) : (
                                <GridItem md={3} xs={3} />
                            )}
                            <GridItem md={4} xs={4}>
                                <Typography className={item.subItem ? classes.customTitleSub : classes.customTitle}>{item.name}</Typography>
                            </GridItem>
                            <GridItem md={5} xs={5}>
                                {!_.isNull(item.value) && (
                                    <CleanableInput
                                        className={classes.input}
                                        defaultValue={item.value}
                                        onChange={(value) => handleChange(value, item)}
                                        error={!!validateResults[`${item.key}_${index}`]}
                                        helperText={validateResults[`${item.key}_${index}`] ? intl.formatMessage({ id: validateResults[`${item.key}_${index}`].message }) : ''}
                                    />
                                )}
                            </GridItem>
                        </GridContainer>
                    ))}
                    <GridContainer className={classes.customFooter}>
                        <GridItem className={classes.customFooterButton}>
                            <Button
                                color="info"
                                round
                                gradient
                                size="md"
                                onClick={handleSubmit}
                                className={classes.customButton}
                            >
                                <b>Apply</b>
                            </Button>
                            <Button
                                color="info"
                                round
                                gradient
                                size="md"
                                onClick={() => setIsOpenConfirmSuccess({ open: true, content: 'リセットして宜しいですか。', type: 'reset' })}
                                className={classes.customButtonReset}
                            >
                                <b>Reset</b>
                            </Button>
                            <Button
                                color="info"
                                round
                                gradient
                                size="md"
                                onClick={() => setIsOpenConfirmSuccess({ open: true, content: '入力した情報をキャンセルして宜しいですか。', type: 'cancel' })}
                                className={classes.customButton}
                            >
                                <b>Cancel</b>
                            </Button>
                        </GridItem>
                    </GridContainer>
                </WrapperContainer>
            </GridContainer>
            <Snackbar
                open={isOpenSnackbar.isOpen}
                onClose={() => setIsOpenSnackbar({ isOpen: false, message: '' })}
                message={isOpenSnackbar.message}
                severity="success"
            />
            <AlertDialog
                title="確認ダイアログ"
                handleClose={() => setIsOpenConfirmSuccess({ open: false, content: '', type: '' })}
                handleOpen={isOpenConfirmSuccess.open}
                handleSubmit={() => handleSuccessDialog(isOpenConfirmSuccess.type)}
                contents={isOpenConfirmSuccess.content}
            />
        </>
    );
};

SettingCustomColor.defaultProps = {
    getSettingCustomColor: () => {},
    updateSettingCustomColor: () => {},
    clearGetSettingColor: () => {},
    clearUpdateSettingColor: () => {},
    customColor: {},
    updateCustomColor: {},
};

SettingCustomColor.propTypes = {
    getSettingCustomColor: PropTypes.func,
    updateSettingCustomColor: PropTypes.func,
    clearGetSettingColor: PropTypes.func,
    clearUpdateSettingColor: PropTypes.func,
    customColor: PropTypes.oneOfType([() => null, PropTypes.object]),
    updateCustomColor: PropTypes.oneOfType([() => null, PropTypes.object]),
};

export default SettingCustomColor;
